import React, { useCallback, useEffect } from 'react';
import useShortcut from '@uber/label-modalities/src/image/labelling/providers/hooks/use-shortcut';
import { styled, useStyletron } from 'baseui';
import { Button, KIND, SIZE } from 'baseui/button';
import { useAtomValue, useSetAtom } from 'jotai';
import _find from 'lodash/find';
import type { StyleObject } from 'styletron-react';
import Loader from '../../../../../../common/loader';
import type { AnnotationUnderUserReviewT } from '../../../../../../common/types/types';
import { getAnnotationStyle } from '../../../../../../common/utils/utils';
import { EVENTS } from '../../../../../labelling-v2/atoms/label-store-dispatch/events';
import { isVideoLabelingM4EnabledAtom } from '../../../../../labelling-v2/atoms/m4-atoms';
import { isSAM3EnabledAtom, polygonEpsilonAtom } from '../../../../../labelling-v2/atoms/tool-atoms';
import { useStageAtoms } from '../../../../../labelling-v2/components/stage-scope/hooks';
import useAnalyticsForSAM from '../../../../analytics/useSAMAnalytics';
import { ANNOTATION_SHAPE_STYLING_TYPE, POINT_BOUNDING_BOX_DEFAULT_DIMENSION } from '../../../../constants';
import { ANNOTATION_TYPE, SAM_ANNOTATION_ACTIONS } from '../../../../constants';
import { useSegmentationBitmapUpdater } from '../../../../providers/hooks/use-segmentation-bitmap-updater';
import useStage from '../../../../providers/hooks/use-stage';
import { useStageOverlayPositioning } from '../../../../providers/hooks/use-stage-overlay-positioning';
import { handleShortcutRegistrationError } from '../../../../providers/utils/shortcut';
import type { BrushDataT, FrameStateT, PolygonDataT } from '../../../../types';
import { DEFAULT_POLYGON_EPSILON } from '../../../../utils/mask-to-polygon';
import { getSAM3LabelAnalyticsPayload } from '../../../toolbar/utils';
import { currentFrameAnnotationBitmapsAtom } from '../../atoms/segmentation-atoms';
import { eraseMaskFromOtherAnnotations, filterSegmentationAnnotations } from '../../canvas/utils';
import Polygon from '../../polygon/polygon';
import { getStylesForStylingType } from '../../utils';
import { isSAMSegmentAnnotation } from './helpers';
const isPolygonReviewAnnotation = (annotation: AnnotationUnderUserReviewT): annotation is Extract<AnnotationUnderUserReviewT, {
  type: typeof ANNOTATION_TYPE.POLYGON | typeof SAM_ANNOTATION_ACTIONS.BOUNDING_BOX_POLYGON;
}> => annotation.type === ANNOTATION_TYPE.POLYGON || annotation.type === SAM_ANNOTATION_ACTIONS.BOUNDING_BOX_POLYGON;
const ActionsWrapper = styled<'div', {
  $overlayStyles: StyleObject;
}>('div', ({
  $theme: theme,
  $overlayStyles: overlayStyles
}) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: `${theme.sizing.scale200}`,
  ...overlayStyles
}));
const LoaderWrapper = styled<'div', {
  $overlayStyles: StyleObject;
}>('div', ({
  $theme: theme,
  $overlayStyles: overlayStyles
}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: `${theme.sizing.scale200}`,
  opacity: 0.5,
  ...overlayStyles
}));
type PropsT = {
  frameData: FrameStateT;
  dimensions: {
    height: number;
    width: number;
  };
};
const convertIntoBounds = (value: number, maxValue: number) => Math.min(maxValue - POINT_BOUNDING_BOX_DEFAULT_DIMENSION, Math.max(0, value));
const BoundingBoxActions = ({
  frameData,
  dimensions
}: PropsT) => {
  const {
    updateAnnotation,
    addAnnotation
  } = useStage();
  const {
    getOverlayStyles
  } = useStageOverlayPositioning();
  const {
    brushToolSettingsAtom,
    currentFrameAtom,
    isLoadingAtom,
    selectedAnnotationLabelAtom,
    currentFrameAnnotationUnderUserReviewAtom,
    annotationsUnderReviewBitmapsAtom,
    dispatchAtom
  } = useStageAtoms();
  const toolLoading = useAtomValue(isLoadingAtom);
  const annotationUnderUserReview = useAtomValue(currentFrameAnnotationUnderUserReviewAtom);
  const selectedAnnotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const currentFrame = useAtomValue(currentFrameAtom);
  const reviewBitmaps = useAtomValue(annotationsUnderReviewBitmapsAtom);
  const annotationBitmap = useAtomValue(currentFrameAnnotationBitmapsAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const {
    selectedTool
  } = useAtomValue(brushToolSettingsAtom);
  const isSAM3Enabled = useAtomValue(isSAM3EnabledAtom);
  const polygonEpsilon = useAtomValue(polygonEpsilonAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const {
    registerHotKeys
  } = useShortcut();
  const {
    sendEventSAMAccepted,
    sendEventSAMRejected,
    sendEventSam3TightnessAdjusted
  } = useAnalyticsForSAM();
  const {
    annotationStore
  } = frameData;
  const [_, theme] = useStyletron();
  const {
    updateBitmapOnAccept,
    removeReviewBitmapOnReject
  } = useSegmentationBitmapUpdater();
  const annotationUnderReviewKeys = Object.keys(annotationUnderUserReview).filter(id => annotationUnderUserReview[id]?.type !== SAM_ANNOTATION_ACTIONS.AUTO_DETECT && annotationUnderUserReview[id]?.type !== SAM_ANNOTATION_ACTIONS.AUTO_DETECT_POLYGON);
  const numberOfAnnotationsUnderReview = annotationUnderReviewKeys.length;
  const onAccept = useCallback((annotationId: string) => {
    const annotationReview = annotationUnderUserReview[annotationId];
    if (!annotationReview?.annotationData?.[0]) return;
    if (selectedAnnotationLabel === null || selectedAnnotationLabel === undefined) return;
    const committedAnnotationLabel = annotationReview.annotationMetaData?.[0]?.annotationLabel ?? selectedAnnotationLabel;
    const automationRequestId = annotationReview.automationRequestId ?? '';
    if (isSAMSegmentAnnotation(annotationReview)) {
      const allAnnotationIDs = Object.keys(annotationStore);
      const existingSegmentationID = _find(allAnnotationIDs, key =>
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      annotationStore[key].annotationType === ANNOTATION_TYPE.SEGMENTATION &&
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      annotationStore[key].annotationLabel.title === committedAnnotationLabel?.title);

      // First, update the bitmap in Jotai cache - this will handle the bitmap merging efficiently
      // and return the base64 representation for storing in Redux
      const updatedBase64String = updateBitmapOnAccept(committedAnnotationLabel, currentFrame, reviewBitmaps);
      let acceptedAnnotationId = existingSegmentationID;
      if (existingSegmentationID !== undefined) {
        const existingSegmentationAnnotation = annotationStore[existingSegmentationID];

        // @ts-expect-error todo(ts-migration) TS18048 'existingSegmentationAnnotation' is possibly 'undefined'.
        switch (existingSegmentationAnnotation.annotationType) {
          case ANNOTATION_TYPE.SEGMENTATION:
            {
              // After updating the bitmap in Jotai, we can now update Redux
              // @ts-expect-error todo(ts-migration) TS2345 Argument of type '{ data: { points: any[]; base64String: string; } | { points: any[]; base64String: string; rotationAngle?: number | undefined; x: number; y: number; width: number; height: number; } |...
              updateAnnotation(existingSegmentationID, {
                ...existingSegmentationAnnotation,
                data: {
                  // @ts-expect-error todo(ts-migration) TS18048 'existingSegmentationAnnotation' is possibly 'undefined'.
                  ...existingSegmentationAnnotation.data,
                  points: [
                  // @ts-expect-error todo(ts-migration) TS18048 'existingSegmentationAnnotation' is possibly 'undefined'.
                  ...(existingSegmentationAnnotation.data.points ?? []),
                  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
                  ...(annotationUnderUserReview[annotationId].annotationData[0].points ?? [])],
                  base64String: updatedBase64String ?? ''
                }
              });
              sendEventSAMAccepted({
                annotationId: existingSegmentationID,
                automationRequestId,
                toolName: selectedTool,
                modelUsed: isSAM3Enabled ? 'SAM3' : 'SAM2'
              });
            }
        }
      } else {
        // Add the annotation - the ID will be returned by addAnnotation
        const newAnnotationId = addAnnotation({
          annotationType: ANNOTATION_TYPE.SEGMENTATION,
          annotationLabel: committedAnnotationLabel,
          data: {
            // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
            ...(annotationUnderUserReview[annotationId].annotationData[0] as BrushDataT),
            base64String: updatedBase64String ?? ''
          }
        });
        acceptedAnnotationId = newAnnotationId;
        sendEventSAMAccepted({
          annotationId: newAnnotationId,
          automationRequestId,
          toolName: selectedTool,
          modelUsed: isSAM3Enabled ? 'SAM3' : 'SAM2'
        });
      }
      const newMaskBitmap = reviewBitmaps[committedAnnotationLabel.title];
      if (newMaskBitmap) {
        eraseMaskFromOtherAnnotations(newMaskBitmap, committedAnnotationLabel.style.strokeColor, filterSegmentationAnnotations(annotationStore), annotationBitmap, acceptedAnnotationId, updateAnnotation);
      }
    } else if (isPolygonReviewAnnotation(annotationReview)) {
      const polygonData = annotationReview.annotationData[0];
      // Add the annotation - the ID will be returned by addAnnotation
      const newAnnotationId = addAnnotation({
        annotationType: ANNOTATION_TYPE.POLYGON,
        annotationLabel: committedAnnotationLabel,
        data: (polygonData as PolygonDataT)
      });
      sendEventSAMAccepted({
        annotationId: newAnnotationId,
        automationRequestId,
        toolName: selectedTool,
        modelUsed: isSAM3Enabled ? 'SAM3' : 'SAM2'
      });
      if (polygonEpsilon !== DEFAULT_POLYGON_EPSILON) {
        sendEventSam3TightnessAdjusted({
          ...getSAM3LabelAnalyticsPayload(committedAnnotationLabel),
          annotationType: 'POLYGON',
          epsilon: polygonEpsilon
        });
      }
    }
    dispatch({
      type: EVENTS.DELETE_ANNOTATION_UNDER_REVIEW,
      annotationIds: [annotationId]
    });
  }, [annotationUnderUserReview, selectedAnnotationLabel, sendEventSAMAccepted, sendEventSam3TightnessAdjusted, polygonEpsilon, dispatch, annotationStore, updateBitmapOnAccept, currentFrame, reviewBitmaps, annotationBitmap, updateAnnotation, addAnnotation, selectedTool, isSAM3Enabled]);
  const onReject = useCallback((annotationId: string) => {
    const annotationReview = annotationUnderUserReview[annotationId];
    const automationRequestId = annotationReview?.automationRequestId ?? '';
    sendEventSAMRejected({
      automationRequestId,
      toolName: selectedTool,
      modelUsed: isSAM3Enabled ? 'SAM3' : 'SAM2'
    });
    const committedAnnotationLabel = annotationReview?.annotationMetaData?.[0]?.annotationLabel;
    committedAnnotationLabel && removeReviewBitmapOnReject(committedAnnotationLabel.title);
    dispatch({
      type: EVENTS.DELETE_ANNOTATION_UNDER_REVIEW,
      annotationIds: [annotationId]
    });
  }, [annotationUnderUserReview, dispatch, isSAM3Enabled, removeReviewBitmapOnReject, selectedTool, sendEventSAMRejected]);
  const onKeyboardAction = useCallback(({
    isAcceptAction
  }) => {
    const annotationId = annotationUnderReviewKeys[0];
    // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    isAcceptAction ? onAccept(annotationId) : onReject(annotationId);
  }, [annotationUnderReviewKeys, onAccept, onReject]);
  useEffect(() => {
    if (toolLoading || numberOfAnnotationsUnderReview === 0) return;
    const {
      errors,
      removeHotKeys
    } = registerHotKeys([{
      keys: ['Enter'],
      onAction: () => {
        onKeyboardAction({
          isAcceptAction: true
        });
      }
    }, {
      keys: ['Escape'],
      onAction: () => {
        onKeyboardAction({
          isAcceptAction: false
        });
      }
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [onKeyboardAction, registerHotKeys, toolLoading, numberOfAnnotationsUnderReview]);
  return <>
      {annotationUnderReviewKeys.map(annotationId => {
      // @ts-expect-error todo(ts-migration) TS2339 Property 'annotationMetaData' does not exist on type 'AnnotationUnderUserReviewT | undefined'.
      const {
        annotationMetaData,
        type,
        annotationData
      } = annotationUnderUserReview[annotationId];
      const metaData = annotationMetaData[0];
      const annotationStyle = getAnnotationStyle(metaData.annotationLabel.style, {
        isFocused: false,
        isLocked: false,
        useStageStrokeWidth: true,
        isM4: isVideoLabelingM4Enabled
      });
      const isPoint = metaData.data.width === 0 && metaData.data.height === 0;
      const rectangleDimensions = isPoint ? {
        x: convertIntoBounds(metaData.data.x - POINT_BOUNDING_BOX_DEFAULT_DIMENSION / 2, dimensions.width),
        y: convertIntoBounds(metaData.data.y - POINT_BOUNDING_BOX_DEFAULT_DIMENSION / 2, dimensions.height),
        height: POINT_BOUNDING_BOX_DEFAULT_DIMENSION,
        width: POINT_BOUNDING_BOX_DEFAULT_DIMENSION
      } : {
        x: metaData.data.x,
        y: metaData.data.y,
        height: metaData.data.height,
        width: metaData.data.width
      };
      const buttonOverlayStyles = getOverlayStyles({
        stageX: rectangleDimensions.x,
        stageY: rectangleDimensions.y,
        translateY: `calc(-100% - ${theme.sizing.scale300})`,
        // Move up by button height + gap
        transformOrigin: 'top left'
      });
      const loaderOverlayStyles = getOverlayStyles({
        stageX: rectangleDimensions.x + rectangleDimensions.width / 2,
        stageY: rectangleDimensions.y + rectangleDimensions.height / 2,
        translateX: '-50%',
        translateY: '-50%',
        transformOrigin: 'top left'
      });
      const isBoundingBoxPolygon = type === SAM_ANNOTATION_ACTIONS.BOUNDING_BOX_POLYGON;
      const polygonData = (annotationData?.[0] as PolygonDataT | undefined);
      const hasPolygonPoints = isBoundingBoxPolygon && polygonData?.points?.length;
      return <React.Fragment key={annotationId}>
            <ActionsWrapper $overlayStyles={buttonOverlayStyles} data-testid="bounding-box-action-wrapper">
              <Button onClick={() => onAccept(annotationId)} size={SIZE.mini} disabled={toolLoading} data-testid="submit-button">
                Submit
              </Button>
              <Button onClick={() => onReject(annotationId)} kind={KIND.secondary} size={SIZE.mini} data-testid="cancel-button">
                Cancel
              </Button>
            </ActionsWrapper>
            {toolLoading ? <LoaderWrapper $overlayStyles={loaderOverlayStyles}>
                <Loader hideLabel size="small" />
                <div>{isSAM3Enabled ? 'Running SAM 3' : 'Running SAM 2.1'}</div>
              </LoaderWrapper> : null}
            {hasPolygonPoints ? <svg width="100%" height="100%">
                <Polygon isDrawing={false} annotation={{
            annotationType: ANNOTATION_TYPE.POLYGON,
            data: (polygonData as PolygonDataT),
            annotationLabel: metaData.annotationLabel
          }} />
              </svg> : <svg width="100%" height="100%">
                <rect {...rectangleDimensions} stroke={annotationStyle.strokeColor} fill={annotationStyle.fillColor} fillOpacity={toolLoading ? 0.2 : 0} opacity={isPoint ? 0 : 1} style={{
            strokeWidth: annotationStyle.strokeWidth
          }} {...getStylesForStylingType(ANNOTATION_SHAPE_STYLING_TYPE.DASHED_BORDER)} />
              </svg>}
          </React.Fragment>;
    })}
    </>;
};
export default BoundingBoxActions;